import LoginAndRegisterButtons from '@app/components/common/LoginAndRegisterButtons/LoginAndRegisterButtons';
import { PLACEMENT } from '@app/components/common/LoginAndRegisterButtons/constants';
import {
  Drawer,
  DrawerHeader,
  HeaderAccountButton,
  HeaderAccountFeatures,
  Portal,
  useInAppSignOut,
  useModal
} from '@cat-ecom/pcc-components';
import { CatHeading } from '@blocks-react/components/Heading';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useHeaderAccountFeatures from '../../hooks/useHeaderAccountFeatures/useHeaderAccountFeatures';
import useLogout from '@app/components/login/hooks/useLogout/useLogout';
import PropTypes from 'prop-types';
import { DealerLocatorModalWrapper } from '@app/components/dealer-locator-react';
import { CreateAccountDescription } from '@app/components/dealer-locator-react/CreateAccountDescription/CreateAccountDescription';
import Conditional from '@app/components/common/Conditional';
import { MODALS_ROOT } from '@app/constants/targets';
import { CSR_ACTIONS } from '@app/components/pages/csr-landing/constants';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import ExceptionFeedbackBySelector from '@app/components/common/Exception/ExceptionFeedbackBySelector';
import ExceptionBlocksFeedbackBySelector from '@app/components/common/ExceptionBlocks/ExceptionBlocksFeedbackBySelector';

const AccountDrawer = ({ closeDrawer, ...drawerProps }) => {
  const { t } = useTranslation();

  const modal = useModal({
    isRequired: true,
    heading: `${t('STEP_1')}: ${t('REGISTER_MODAL_SELECT_DEALER_STORE')}`,
    backButton: false,
    size: 'md'
  });

  const heading = t('CAT_SHOPPING_PREFERENCES_SELECT_STORE');
  const transactAsGuestModal = useModal({
    isRequired: false,
    heading,
    backButton: false,
    size: 'md'
  });

  const userType = useSelector(s => s.common?.userType);
  const isBuyOnBehalf = useSelector(s => s.common.isBuyOnBehalf);
  const isCatCSR = useSelector(s => s.common?.isCatCSR);
  const isLoggedIn = userType && userType !== 'G';
  const features = useHeaderAccountFeatures(
    isLoggedIn,
    closeDrawer,
    transactAsGuestModal
  );
  const { logout } = useLogout();

  const { getInAppCookie } = useInAppSignOut();
  const inApp = getInAppCookie();

  const timeoutErrorSelector = state =>
    state?.errors?.[ERROR_DOMAIN.LOGIN_REGISTRATION]?.[ERROR_PATH.TIMEOUT];
  const timeoutError = useSelector(timeoutErrorSelector);

  const inlineErrorSelector = state =>
    state?.errors?.[ERROR_DOMAIN.LOGIN_REGISTRATION]?.[
      ERROR_PATH.LOGIN_INLINE_ERROR
    ] ??
    state?.errors?.[ERROR_DOMAIN.LOGIN_REGISTRATION]?.[
      ERROR_PATH.REGISTRATION_INLINE_ERROR
    ];
  const inlineError = useSelector(inlineErrorSelector);

  const renderError = () => {
    if (timeoutError) {
      return (
        <ExceptionFeedbackBySelector
          className={'my-3'}
          selector={timeoutErrorSelector}
          showContactUs
          showDate
        />
      );
    }
    if (inlineError) {
      return (
        <ExceptionBlocksFeedbackBySelector
          className={'my-3'}
          selector={inlineErrorSelector}
          showContactUs
          showRefresh
          showReferenceId
        />
      );
    }
    return null;
  };

  return (
    <>
      {isCatCSR && (
        <Portal id={MODALS_ROOT}>
          <DealerLocatorModalWrapper
            modal={transactAsGuestModal}
            bobRedirect={CSR_ACTIONS.TRANSACT_AS_GUEST}
          />
        </Portal>
      )}
      <Drawer {...drawerProps}>
        <DrawerHeader>
          <CatHeading variant="title" data-testid="cat-header-account-title">
            {t('FOOTER_ACCOUNT')}
          </CatHeading>
        </DrawerHeader>
        {renderError()}
        <Conditional test={!isLoggedIn}>
          <LoginAndRegisterButtons
            disclaimerPlacement={PLACEMENT.BOTTOM}
            closeDrawer={closeDrawer}
            openDealerLocator={modal.initiatorProps.onClick}
          />
        </Conditional>
        <HeaderAccountFeatures features={features} />
        <Conditional test={isLoggedIn && !isBuyOnBehalf && !inApp}>
          <HeaderAccountButton label={t('HEADER_SIGN_OUT')} onClick={logout} />
        </Conditional>
      </Drawer>
      <DealerLocatorModalWrapper isRegistration modal={modal}>
        <CreateAccountDescription />
      </DealerLocatorModalWrapper>
    </>
  );
};

export default AccountDrawer;

AccountDrawer.propTypes = {
  closeDrawer: PropTypes.func
};
